import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { WorkspaceWindowTypeEnumType } from '@mark43/cad-api';
import React, { createContext, useEffect, useRef, useState } from 'react';
import { useAppName } from '../../app-name/app-name';
import { useWorkspaceWindowType } from '../../common/hooks/use-workspace-window-type';
import { getGlobalProperties } from '../../global-properties/global-properties';
import { createPendoTrackingInstance } from './pendo-utils';
import { UserActionTrackerInstance } from './types';

export const UserActionTrackerContext = createContext<UserActionTrackerInstance | undefined>(
    undefined
);

export function UserActionTrackerProvider({ children }: { children: React.ReactNode }) {
    const appName = useAppName();
    const { pendoEnabled, pendoTrackEventsEnabled } = useEnabledFeatures();
    const workspaceWindowType = useWorkspaceWindowType();
    const workspaceWindowTypeRef = useRef<WorkspaceWindowTypeEnumType>(workspaceWindowType);
    const [userActionTrackerInstance, setUserActionTrackerInstance] = useState<
        UserActionTrackerInstance | undefined
    >(undefined);

    useEffect(() => {
        async function load() {
            if (window.pendo && window.pendo.isReady && window.pendo.isReady()) {
                const globalProperties = await getGlobalProperties();

                const {
                    VERSION: rendererVersion,
                    ELECTRON_MAIN_VERSION: appVersion,
                } = globalProperties;

                setUserActionTrackerInstance(
                    createPendoTrackingInstance(window.pendo, {
                        appName,
                        appVersion,
                        rendererVersion,
                        workspaceWindowTypeRef,
                    })
                );
                return true;
            }
            return false;
        }

        if (!pendoEnabled || !pendoTrackEventsEnabled) {
            return;
        }
        // Retry up to 5 times
        const maxAttempts = 5;
        let attempts = 1;
        const interval = setInterval(async () => {
            if ((await load()) || ++attempts > maxAttempts) {
                clearInterval(interval);
            }
        }, 400);

        return () => clearInterval(interval);
    }, [appName, pendoEnabled, pendoTrackEventsEnabled]);

    return (
        <UserActionTrackerContext.Provider value={userActionTrackerInstance}>
            {children}
        </UserActionTrackerContext.Provider>
    );
}
